import { Button } from '@parrot-software/design-system';
import { CloudOff, RotateCcw } from '@parrot-software/design-system/icons';
import { cn } from '@parrot-software/utils';

/** Props for ErrorState component. */
export type ErrorStateProps = {
  bordered?: boolean;
  description?: string;
  onRetry?: () => void;
  retryLabel?: string;
  title: string;
};

/**
 * ErrorState - Generic error state component for subscription sections.
 *
 * Displays a cloud-off icon, title, optional description, and an optional retry action.
 * Reusable across payment history, invoice data, and other subscription tabs.
 * `bordered` (default `true`) renders the rounded border + shadow card frame; pass
 * `false` to render the state inline without the frame (e.g. inside the checkout wizard).
 */
export const ErrorState = ({ title, description, onRetry, retryLabel, bordered = true }: ErrorStateProps) => (
  <div
    className={cn('flex flex-col h-[50vh] items-center justify-center gap-6 p-12', {
      'rounded-xl border shadow-xs': bordered,
    })}
    data-testid="subscription-error-state"
  >
    <div className="flex flex-col items-center gap-3 max-w-[350px]">
      <CloudOff className="h-9 w-9 text-destructive" strokeWidth={1.5} />
      <p className="text-lg font-medium text-center">{title}</p>
      {description && <p className="text-sm text-muted-foreground text-center">{description}</p>}
    </div>

    {onRetry && (
      <Button onClick={onRetry}>
        <RotateCcw className="mr-2 h-4 w-4" />
        {retryLabel}
      </Button>
    )}
  </div>
);
